/**
 * Orders API
 *
 * Endpoints:
 *   POST api/v1/orders/price        - getCartPrice, getOrderPrice
 *   POST api/v1/orders               - placeNewOrder
 *   POST api/v1/orders/:id/ready_for_processing - readyForProcessing
 */
import uniqBy from 'utils/uniqBy';
import { getBrowser, getDevice, getOperatingSystem } from 'utils/clientInfo';
import { isUrlValid } from 'utils/validator';
import produce from 'utils/produce';
import store from 'utils/store';

import { apiClientPostRequest } from '../lib/backendRequest';
import { GIFTNOTE_SKU } from '../lib/giftnote';
import { orderPriceRequest } from '../lib/orderPriceRequest';
import uploadApi from './upload';

import SETTINGS from '../settings';

export const ORDER_STATUS = {
  0: 'Uploading',
  5: 'Awaiting payment',
  10: 'Ready for processing',
  20: 'Processing',
  30: 'Processed',
  40: 'Partially shipped',
  50: 'Shipped',
  60: 'Partially in transit',
  65: 'In transit',
  70: 'Partially out for delivery',
  75: 'Out for delivery',
  80: 'Partially delivery attempt failed',
  85: 'Delivery attempt failed',
  90: 'Delivered',
  100: 'Cancelled',
  110: 'Exception',
};

const friendlyPriceError = (countryCode, items, error) => {
  if (error.code === 400) {
    const matchArray = error.body.match('Product "(.*)" is not available');
    if (matchArray) {
      const item = items.find(i => i.productId === matchArray[1]);
      const productName = item ? item.name : matchArray[1];

      return `Oh no! We’re sorry, “${productName}” isn’t available in your country.`;
    }
  }
  if (error.body) {
    return error.body;
  }

  return 'Oh no! We’re sorry, something has gone wrong. We’re working to fix the issue. In the meantime, you could try refreshing your page or beginning again.';
};

const PRICE_MISMATCH = /Overall price \d+\.\d+ does not match expected price \d+\.\d+$/;

// `body` is a number for a non-text error response (e.g. a proxy 502 page).
const isPriceMismatch = error => typeof error.body === 'string' && PRICE_MISMATCH.test(error.body);

/**
 * checkout_payment_failure error_code for a failed order POST: the backend `reason` (Stripe
 * decline code, Amazon Pay decline, refused coupon) when it sent one, else the failure class.
 */
export const orderErrorCode = error => {
  if (error.reason) return error.reason;
  if (error.isNetworkError || error.isAborted) return 'network_error';
  if (error.code === 402) return 'card_declined';
  if (error.code === 503) return 'gateway_error';
  if (isPriceMismatch(error)) return 'price_mismatch';
  if (error.code === 400) return 'order_rejected';
  return 'server_error';
};

const friendlyOrderError = error => {
  if (isPriceMismatch(error)) {
    return 'Oh no! There seems to be an error with the price displayed. Please refresh your page to see the correct price.';
  }
  if (typeof error.body === 'string' && error.body) {
    return error.body;
  }
  return 'Oh no! We’re sorry, something has gone wrong. We’re working to fix the issue. In the meantime, you could try refreshing your page or beginning again.';
};

const formatPriceData = data => {
  const giftNote = data.items.reduce((price, item) => {
    if (item.id === GIFTNOTE_SKU) {
      return price + item.all;
    }
    return price;
  }, 0);

  return {
    ...data,
    giftNote,
  };
};

const ordersApi = countryCode => ({
  /**
   * Returns { priceData }
   *
   * @param {Array<Object>} items
   */
  async getCartPrice(items) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/orders/price',
        orderPriceRequest(items, countryCode),
      );

      const json = await response.json();

      return { data: formatPriceData(json.data) };
    } catch (error) {
      return { error: friendlyPriceError(countryCode, items, error) };
    }
  },

  /**
   * Returns { priceData }
   *
   * @param {string} accessToken
   * @param {Array<Object>} items
   * @param {number | string} shippingType
   * @param {string} promoCode
   * @param {string} [paymentMethod] 'ach' quotes the bank discount for an eligible account.
   */
  async getOrderPrice(accessToken, items, shippingType = -1, promoCode = '', paymentMethod) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/orders/price',
        orderPriceRequest(items, countryCode, shippingType, promoCode, paymentMethod),
        accessToken ? { Authorization: `Token ${accessToken}` } : {},
      );
      const json = await response.json();
      return { data: formatPriceData(json.data) };
    } catch (error) {
      return { error: friendlyPriceError(countryCode, items, error), status: error.code, reason: error.reason };
    }
  },

  /**
   * Returns { priceData }
   *
   * @param {string} accessToken
   * @param {Array<Object>} items
   * @param {number | string} shippingType
   * @param {string} promoCode
   * @param {Object} paymentData
   * @param {number} price
   * @param {Object} shippingAddress
   * @param {Object} orderInfo
   * @param {Object} [billingAddress] Optional distinct billing address, sent only when provided.
   * @returns {Promise<Object>} Order data or an error with errorCode (see orderErrorCode).
   *   Post-creation errors include data and orderCreated.
   */
  async placeNewOrder(
    accessToken,
    items,
    shippingType,
    promoCode,
    paymentData,
    price,
    shippingAddress,
    orderInfo = {},
    billingAddress = undefined,
  ) {
    /**
     * Extract all of urls and map only required fields from item
     */
    let uploadItems = [];
    let products = items.map(item => {
      let data;

      if (item.data) {
        // don't send favorite & photos data in the order
        data = { ...item.data, favorite: undefined, photos: undefined };

        const remapPlaceholders = page => {
          if (!page.placeholders) {
            return undefined;
          }

          return page.placeholders.map(placeholder => {
            if (placeholder.type === 'photo' || placeholder.type === 'media') {
              let value = placeholder.value || undefined;
              let defaultValue = placeholder.defaultValue || undefined;
              const mimeType = placeholder.photo_type || undefined;

              //
              // IMPORTANT BUGFIX
              // In some cases, photo.defaultValue is not uses properly, it is hardcode fixed
              //
              if (value) {
                const checkDefaultValue = (fileNameOptions = []) => {
                  const isDangerousAsset = fileNameOptions.some(searchText => {
                    return value.indexOf(searchText) > 0;
                  });
                  return isDangerousAsset && !mimeType;
                };

                if (checkDefaultValue(['/ancestry-woman.png', '/female-avatar.png'])) {
                  value = undefined;
                  defaultValue = 'female-avatar.png';
                  console.log('Female Avatar Updated');
                } else if (checkDefaultValue(['/ancestry-man.png', '/male-avatar.png'])) {
                  value = undefined;
                  defaultValue = 'male-avatar.png';
                  console.log('Male Avatar Updated');
                }
              }

              // When placeholder already has mediumId, use this as value
              // Then its not defaultValue
              if (placeholder.mediumId) {
                value = placeholder.mediumId;
                defaultValue = undefined;
              } else if (isUrlValid(value)) {
                // When value isValidUrl, it means upload is required
                uploadItems.push({
                  url: value,
                  mimeType,
                });
              }

              return {
                id: placeholder.id,
                type: placeholder.type,
                center: placeholder.center,
                value,
                defaultValue,
                orientation: placeholder.orientation,
                scale: placeholder.scale,
                override: placeholder.override ?? undefined,
                photo_width: placeholder.photo_width ?? undefined,
                photo_height: placeholder.photo_height ?? undefined,
                photo_datetime: placeholder.photo_datetime ?? undefined,
                photo_tags: placeholder.photo_tags ?? undefined,
                photo_type: placeholder.photo_type ?? undefined,
                polotno: placeholder.polotno ?? undefined,
              };
            }
            return placeholder;
          });
        };

        if (data.cover) {
          data.cover = produce(data.cover, draft => {
            draft.placeholders = remapPlaceholders(data.cover);
          });
        }

        if (data.pages) {
          data.pages = produce(data.pages, draft => {
            for (let index = 0; index < data.pages.length; index += 1) {
              draft[index].placeholders = remapPlaceholders(data.pages[index]);
            }
          });
        }
      }

      if (item.bundle) {
        const secondaryProduct = item.bundle.secondaryProducts[item.bundle.secondaryProductIndex];

        return {
          id: item.bundle.id,
          quantity: item.quantity,
          group: item.group ?? undefined,
          products: [
            {
              id: item.productId,
              version: item.version,
              data,
            },
            {
              id: secondaryProduct.id,
              version: secondaryProduct.version,
              data: secondaryProduct.data,
            },
          ],
        };
      }

      return {
        id: item.productId,
        quantity: item.quantity,
        version: item.version,
        group: item.group ?? undefined,
        draft_id: item.draftId ?? undefined,
        data,
      };
    });

    uploadItems = uniqBy(uploadItems, 'url');

    const paraboUploadApi = uploadApi(accessToken, countryCode);

    const { data: media, error: mediaError } = await paraboUploadApi.getRemoteUrls(
      'photo',
      uploadItems,
    );

    if (mediaError) {
      console.error(mediaError);
      return {
        error:
          'Oh no! We’re sorry, it looks like there has been a problem uploading your photos. Please try again.',
        errorCode: 'upload_error',
      };
    }

    /**
     * Replace photo url with medium id
     */
    let productsString = JSON.stringify(products);
    media.forEach(medium => {
      productsString = productsString.split(`"${medium.url}"`).join(`${medium.id}`);
    });
    products = JSON.parse(productsString);

    let order;
    try {
      const response = await apiClientPostRequest(
        'api/v1/orders',
        {
          products,
          country_alpha2: countryCode,
          shipping_type: shippingType === -1 ? undefined : shippingType,
          shipping_address: shippingAddress,
          ...(billingAddress ? { billing_address: billingAddress } : {}),
          payment_data: {
            expected_price: price,
            promo_code: promoCode,
            ...(paymentData ?? {}),
          },
          system_info: {
            Device: getDevice() || '---',
            Browser: getBrowser() || '---',
            'Operating System': getOperatingSystem() || '---',
            Payment: '---',
            'Client Version': SETTINGS.VERSION,
            ...orderInfo,
          },
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      order = json.data;

      const { error: uploadError } = await paraboUploadApi.remoteUpload(
        order.id,
        media.map(medium => medium.id),
      );

      if (uploadError) {
        console.error(uploadError);
        return {
          data: order,
          orderCreated: true,
          error:
            'Oh no! We’re sorry, it looks like there has been a problem uploading your photos. Please try again.',
        };
      }

      if (uploadItems.length === 0 && json.data.status === 0) {
        await this.readyForProcessing(accessToken, order.id);
      }

      const orderedItems = (order.meta_data?.payment_info?.items || []).map(orderedItem => {
        const itemData = items.find(item => item.productId === orderedItem.id);

        if (itemData) {
          return {
            ...orderedItem,
            name: itemData.name,
          };
        }

        return orderedItem;
      });

      store.remove("traceId");

      return {
        data: {
          ...order,
          externalOrderId: order.external_id,
          orderedItems,
        },
      };
    } catch (error) {
      return {
        data: order,
        error: friendlyOrderError(error),
        errorCode: orderErrorCode(error),
        orderCreated: Boolean(order),
      };
    }
  },

  /**
   *
   * @param {string} accessToken
   * @param {number} orderId
   */
  async readyForProcessing(accessToken, orderId) {
    try {
      const response = await apiClientPostRequest(
        `api/v1/orders/${orderId}/ready_for_processing`,
        {},
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      await response.json();

      return { data: true };
    } catch (e) {
      return { error: e.code };
    }
  },
});

export default ordersApi;
